// api/payment.api.js - Consolidated and UPDATED for Razorpay-only
import API_BASE, {getAuthHeaders} from "./api";

export class PaymentService {
    // ✅ UPDATED: Create Razorpay order (wrapper for CheckoutService)
    static async createRazorpayOrder(orderId) {
        try {
            const response = await fetch(`${API_BASE}/payments/create-razorpay-order`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({orderId}),
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to create payment order');
            }

            return data;
        } catch (error) {
            console.error('Create Razorpay order error:', error);
            return {success: false, error: error.message};
        }
    }

    // ✅ UPDATED: Verify Razorpay payment
    static async verifyRazorpayPayment(razorpayPaymentId, razorpayOrderId, razorpaySignature) {
        try {
            const response = await fetch(`${API_BASE}/payments/verify-razorpay`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    razorpay_payment_id: razorpayPaymentId,
                    razorpay_order_id: razorpayOrderId,
                    razorpay_signature: razorpaySignature
                }),
                credentials: 'include'
            });

            const data = await response.json();
            // console.log(data.data)
            if (!response.ok) {
                throw new Error(data.message || data.error || 'Payment verification failed');
            }

            return data;
        } catch (error) {
            console.error('Verify Razorpay payment error:', error);
            return {success: false, error: error.message};
        }
    }

    // Get payment status by payment ID (legacy)
    static async getPaymentStatus(paymentId) {
        try {
            const response = await fetch(`${API_BASE}/payments/${paymentId}/status`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to get payment status');
            }

            return data;
        } catch (error) {
            console.error('Get payment status error:', error);
            return {success: false, error: error.message};
        }
    }

    // Get payment status by order ID
    static async getPaymentStatusByOrder(orderId) {
        try {
            const response = await fetch(`${API_BASE}/payments/status/${orderId}`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to get payment status');
            }

            return data;
        } catch (error) {
            console.error('Get payment status by order error:', error);
            return {success: false, error: error.message};
        }
    }

    // Get all payments for an order
    static async getOrderPayments(orderId) {
        try {
            const response = await fetch(`${API_BASE}/payments/order/${orderId}/all`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to get order payments');
            }

            return data;
        } catch (error) {
            console.error('Get order payments error:', error);
            return {success: false, error: error.message};
        }
    }

    // Confirm payment (legacy - kept for backward compatibility)
    static async confirmPayment(paymentId, razorpayPaymentId, provider = 'razorpay') {
        console.warn('Deprecated: Use verifyRazorpayPayment instead');
        return this.verifyRazorpayPayment(razorpayPaymentId, '', '');
    }

    // Check payment gateway health
    static async checkHealth() {
        try {
            const response = await fetch(`${API_BASE}/payments/health`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Payment health check error:', error);
            return {success: false, error: error.message};
        }
    }

    // Get available payment methods
    static async getPaymentMethods() {
        try {
            const response = await fetch(`${API_BASE}/payments/methods`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                // Return default Razorpay method
                return {
                    success: true,
                    data: [
                        {
                            id: 'razorpay',
                            provider: 'razorpay',
                            name: 'Razorpay',
                            description: 'Pay via UPI, Cards, Net Banking',
                            icon: 'https://razorpay.com/favicon.png',
                            active: true
                        }
                    ]
                };
            }

            return data;
        } catch (error) {
            console.error('Get payment methods error:', error);
            return {
                success: false,
                error: error.message,
                data: [] // Return empty array on error
            };
        }
    }

    // ✅ Cancel payment
    static async cancelPayment(data) {
        try {
            const response = await fetch(`${API_BASE}/payments/cancel`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data),
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || result.error || 'Failed to cancel payment');
            }

            return result;
        } catch (error) {
            console.error('Cancel payment error:', error);
            return {success: false, error: error.message};
        }
    }

    // ✅ Check if payment can be cancelled
    static async checkCancellable(orderId) {
        try {
            const response = await fetch(`${API_BASE}/payments/can-cancel/${orderId}`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || result.error || 'Failed to check cancellation status');
            }

            return result;
        } catch (error) {
            console.error('Check cancellable error:', error);
            return {success: false, error: error.message};
        }
    }

    // ✅ Handle payment failure
    static async markPaymentFailed(orderId, errorMessage) {
        try {
            const response = await fetch(`${API_BASE}/checkout/payment-failed`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({orderId, errorMessage}),
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || result.error || 'Failed to mark payment as failed');
            }

            return result;
        } catch (error) {
            console.error('Mark payment failed error:', error);
            return {success: false, error: error.message};
        }
    }

}

// Export as default
export default PaymentService;